import { useEffect, useState } from "react";
import { Settings as SettingsAPI, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Warning, Copy } from "@phosphor-icons/react";

const DAYS = [
  { i: 0, label: "Mon" },
  { i: 1, label: "Tue" },
  { i: 2, label: "Wed" },
  { i: 3, label: "Thu" },
  { i: 4, label: "Fri" },
  { i: 5, label: "Sat" },
  { i: 6, label: "Sun" },
];

const COMMON_TZ = [
  "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
  "America/Toronto", "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "Europe/Madrid", "Africa/Johannesburg", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore",
  "Asia/Hong_Kong", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland", "UTC",
];

export default function SettingsPage() {
  const [s, setS] = useState(null);
  useEffect(() => { SettingsAPI.get().then(setS); }, []);
  if (!s) return <div className="p-10 text-sm text-neutral-500">Loading...</div>;

  const save = async () => {
    try {
      const updated = await SettingsAPI.update(s);
      setS(updated);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };

  const toggleDay = (i) => {
    const days = new Set(s.work_days || [0, 1, 2, 3, 4]);
    if (days.has(i)) days.delete(i); else days.add(i);
    setS({ ...s, work_days: Array.from(days).sort() });
  };

  const workDays = new Set(s.work_days || [0, 1, 2, 3, 4]);
  const tzOptions = COMMON_TZ.includes(s.workspace_tz) || !s.workspace_tz ? COMMON_TZ : [s.workspace_tz, ...COMMON_TZ];

  return (
    <div data-testid="settings-page">
      <PageHeader overline="Configure · Credentials" title="Settings" testId="settings-header" />
      <div className="p-10 max-w-2xl space-y-6">
        <Card className="p-8 rounded-md border-neutral-200">
          <div className="overline mb-4">Business profile</div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Business name</Label><Input value={s.business_name || ""} onChange={e => setS({...s, business_name: e.target.value})} data-testid="s-business" /></div>
            <div><Label>Sender name</Label><Input value={s.sender_name || ""} onChange={e => setS({...s, sender_name: e.target.value})} data-testid="s-sender" /></div>
            <div><Label>Sender email (for replies)</Label><Input value={s.sender_email || ""} onChange={e => setS({...s, sender_email: e.target.value})} data-testid="s-email" /></div>
            <div><Label>Booking slug</Label><Input value={s.booking_slug || "book"} onChange={e => setS({...s, booking_slug: e.target.value})} data-testid="s-slug" /></div>
          </div>
        </Card>

        <Card className="p-8 rounded-md border-neutral-200" data-testid="working-hours-card">
          <div className="overline mb-2">Working hours</div>
          <p className="text-sm text-neutral-500 mb-4">Public booking slots only appear during these hours on the days you pick. Slots already taken by an existing appointment are hidden automatically.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start hour (24h)</Label>
              <Input type="number" min="0" max="23" value={s.work_start_hour ?? 9} onChange={e => setS({...s, work_start_hour: Number(e.target.value)})} data-testid="s-work-start" />
            </div>
            <div>
              <Label>End hour (24h, exclusive)</Label>
              <Input type="number" min="1" max="24" value={s.work_end_hour ?? 17} onChange={e => setS({...s, work_end_hour: Number(e.target.value)})} data-testid="s-work-end" />
            </div>
            <div>
              <Label>Slot length (minutes)</Label>
              <Input type="number" min="15" max="180" step="15" value={s.slot_minutes ?? 30} onChange={e => setS({...s, slot_minutes: Number(e.target.value)})} data-testid="s-slot-min" />
            </div>
            <div>
              <Label>Your time zone</Label>
              <Select value={s.workspace_tz || "UTC"} onValueChange={v => setS({...s, workspace_tz: v})}>
                <SelectTrigger data-testid="s-ws-tz"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {tzOptions.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-5">
            <Label>Working days</Label>
            <div className="flex gap-2 mt-2" data-testid="s-work-days">
              {DAYS.map(d => (
                <button
                  key={d.i}
                  type="button"
                  onClick={() => toggleDay(d.i)}
                  data-testid={`day-${d.label.toLowerCase()}`}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                    workDays.has(d.i)
                      ? "bg-neutral-900 text-white border-neutral-900"
                      : "border-neutral-300 hover:border-neutral-900 text-neutral-600"
                  }`}
                >{d.label}</button>
              ))}
            </div>
          </div>
        </Card>

        <Card className="p-8 rounded-md border-neutral-200">
          <div className="overline mb-2">Twilio SMS</div>
          <p className="text-sm text-neutral-500 mb-4">Add your own Twilio credentials to send real SMS. Without these, SMS sends are mocked (logged to your inbox as "sent" for pipeline tracking).</p>
          <div className="grid grid-cols-1 gap-4">
            <div><Label>Account SID</Label><Input value={s.twilio_account_sid || ""} onChange={e => setS({...s, twilio_account_sid: e.target.value})} placeholder="ACxxxx..." data-testid="s-tw-sid" /></div>
            <div><Label>Auth Token</Label><Input type="password" value={s.twilio_auth_token || ""} onChange={e => setS({...s, twilio_auth_token: e.target.value})} data-testid="s-tw-token" /></div>
            <div><Label>From phone (E.164)</Label><Input value={s.twilio_phone || ""} onChange={e => setS({...s, twilio_phone: e.target.value})} placeholder="+14155552671" data-testid="s-tw-phone" /></div>
          </div>
        </Card>

        <Card className="p-8 rounded-md border-neutral-200">
          <div className="overline mb-2">Reply Detection Webhook</div>
          <p className="text-sm text-neutral-500 mb-4">
            Point your inbound-email service (Mailgun Routes, SendGrid Inbound Parse, Zapier "Email Parser", Postmark) at this URL.
            When any prospect replies, they'll auto-flip to "Replied" and queued follow-ups get cancelled.
            The payload should include a <span className="font-mono">from</span> field with the sender's email.
          </p>
          <div className="flex items-center gap-2">
            <Input readOnly value={`${API}/webhooks/inbound-email`} className="font-mono text-xs" data-testid="webhook-url" />
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${API}/webhooks/inbound-email`); toast.success("Copied"); }} data-testid="copy-webhook-btn">
              <Copy size={14} className="mr-2" />Copy
            </Button>
          </div>
          <p className="text-xs text-neutral-500 mt-3">
            You can also mark a prospect as replied manually from the Prospects table when their status is "contacted".
          </p>
        </Card>

        <Card className="p-6 rounded-md border-amber-200 bg-amber-50">
          <div className="flex gap-3">
            <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <div className="font-medium mb-1">On cold email sending</div>
              <div>Cold email blasts are not sent from the platform. When you press <em>Send</em> on an email draft, Prospexus opens your default mail client (Gmail, Outlook, Apple Mail) with the message pre-filled — you send it from your own inbox. Booking confirmations, however, are transactional and sent automatically. Confirmation emails include a read-receipt pixel so you'll see when they were opened (visible in the Inbox).</div>
            </div>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button className="bg-neutral-900 hover:bg-black text-white" onClick={save} data-testid="save-settings-btn">Save settings</Button>
        </div>
      </div>
    </div>
  );
}
